import { locale } from "@locale";
import { format, isValid, parse } from "date-fns";

/*
 * The program state lives in the hash, so it survives a reload and can be shared without
 * creating new cache entries in Front Door (the hash is never sent to the server).
 *
 * Format: #<date>&kino=<cinema>&kino=<cinema>&sjanger=<genre> (the keys are Danish on kino.dk, see locale.program.hashKeys)
 * - The first segment is the date (dd.MM.yyyy) or "all", without a key, so old links (#20.10.2026, #all) still work
 * - All parts are optional
 * - Segments we don't know (e.g. #trailerXYZ) are kept as is
 */

export type ProgramHashState = {
	date: Date | "all" | null; // null = not in the hash
	cinemas: string[] | null;
	genres: string[] | null;
};

export type ParsedProgramHash = ProgramHashState & {
	other: string[]; // unknown segments, kept raw so they are written back unchanged
};

/**
 * The chosen cinemas are also saved in a cookie (in the same format as the hash, without the "#"),
 * so they are remembered across visits. One year, the same as the location.
 * The cookie is wiped whenever a new location is saved (see LocationContext).
 */
export const PROGRAM_FILTER_COOKIE_MAX_AGE = 31_556_926;
export const SELECTED_CINEMAS_COOKIE = "programCinemas";

const DATE_FORMAT = "dd.MM.yyyy";
const DATE_SEGMENT = /^\d{2}\.\d{2}\.\d{4}$/;
const ALL_DATES = "all";
const CINEMA_KEY = locale.program.hashKeys.cinema;
const GENRE_KEY = locale.program.hashKeys.genre;

/**
 * Reports what is in the hash. Doesn't decide anything: a date in the past is still returned,
 * and anything missing is null. Choosing the initial date (today, cookie fallback) is up to the caller.
 */
export function parseProgramHash(hash: string): ParsedProgramHash {
	const result: ParsedProgramHash = { date: null, cinemas: null, genres: null, other: [] };
	const segments = hash.replace(/^#/, "").split("&").filter(s => s !== "");

	if (segments[0] === ALL_DATES) {
		result.date = ALL_DATES;
		segments.shift();
	} else if (DATE_SEGMENT.test(segments[0] ?? "")) {
		const date = parse(segments.shift()!, DATE_FORMAT, new Date());
		result.date = isValid(date) ? date : null;
	}

	for (const segment of segments) {
		const eq = segment.indexOf("=");
		const key = eq === -1 ? segment : segment.slice(0, eq);
		const value = eq === -1 ? null : decodeValue(segment.slice(eq + 1));

		if (key === CINEMA_KEY && value !== null) {
			if (value !== "") {
				result.cinemas = [...(result.cinemas ?? []), value];
			}
		} else if (key === GENRE_KEY && value !== null) {
			if (value !== "") {
				result.genres = [...(result.genres ?? []), value];
			}
		} else {
			result.other.push(segment);
		}
	}
	return result;
}

/**
 * Builds the hash, including the "#". Returns "" when there is nothing to put in it.
 * null and empty lists are left out.
 */
export function buildProgramHash(state: ProgramHashState, other: string[] = []): string {
	const segments: string[] = [];
	if (state.date === ALL_DATES) {
		segments.push(ALL_DATES);
	} else if (state.date !== null && isValid(state.date)) {
		segments.push(format(state.date, DATE_FORMAT));
	}
	state.cinemas?.forEach(cinema => segments.push(`${CINEMA_KEY}=${encodeValue(cinema)}`));
	state.genres?.forEach(genre => segments.push(`${GENRE_KEY}=${encodeValue(genre)}`));
	segments.push(...other);
	return segments.length > 0 ? `#${segments.join("&")}` : "";
}

/**
 * Reads the current hash, replaces only the parts given and writes it back if anything changed.
 * A part that is left out is kept, null or an empty list removes it.
 */
export function updateProgramHash(partial: Partial<ProgramHashState>): void {
	if (typeof window === "undefined") {
		return;
	}
	const { other, ...current } = parseProgramHash(window.location.hash);
	const given = Object.fromEntries(Object.entries(partial).filter(([, value]) => value !== undefined));
	const next: ProgramHashState = { ...current, ...given };

	const newHash = buildProgramHash(next, other);
	if (newHash === normalizeHash(window.location.hash)) {
		return;
	}
	// hash is client-only state - use the History API directly (Next.js keeps its router state in sync)
	// instead of router.replace, which triggers an RSC fetch. If that fetch hits a newer build than the
	// cached HTML, Next hard-reloads the same URL, which loops while the CDN serves the stale page
	window.history.replaceState(null, "", newHash || window.location.pathname + window.location.search);
}

//#region [Other]
function encodeValue(value: string): string {
	// "+" for spaces reads better in the address bar than %20
	return encodeURIComponent(value).replace(/%20/g, "+");
}

/** null if the value can't be decoded (e.g. a broken, hand-edited link) */
function decodeValue(value: string): string | null {
	try {
		return decodeURIComponent(value.replace(/\+/g, " "));
	} catch {
		return null;
	}
}

/** location.hash is "" for both no hash and a lone "#" */
function normalizeHash(hash: string): string {
	return hash === "#" ? "" : hash;
}
//#endregion
